export type VariationAttributeValue = {
  id: number
  name: string
  slug?: string
  imageUrl?: string | null
  /** Added to the product price when this value is selected. */
  extraPrice?: number | null
}

export type VariationAttributeConfig = {
  attributeId: number
  slug: string
  name: string
  values: VariationAttributeValue[]
  /** `attribute` = extras on each value. Omitted = one price per combination. */
  pricingMode?: 'attribute' | 'combination'
}

export type VariantOptions = Record<string, string>

export function productIsVariable(product: {
  isVariable?: boolean | null
} | null | undefined): boolean {
  return product?.isVariable === true
}

export function generateVariantOptionCombos(
  axes: VariationAttributeConfig[],
): VariantOptions[] {
  if (!axes.length) return []
  let combos: VariantOptions[] = [{}]
  for (const axis of axes) {
    if (!axis.values?.length) continue
    const next: VariantOptions[] = []
    for (const partial of combos) {
      for (const value of axis.values) {
        next.push({ ...partial, [axis.slug]: value.name })
      }
    }
    combos = next
  }
  return combos.filter((c) => Object.keys(c).length > 0)
}

export function optionsMatch(
  a: VariantOptions | null | undefined,
  b: VariantOptions | null | undefined,
): boolean {
  if (!a || !b) return false
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const key of keys) {
    if ((a[key] || '') !== (b[key] || '')) return false
  }
  return keys.size > 0
}

export function variantLabel(
  options: VariantOptions | null | undefined,
  axes?: VariationAttributeConfig[] | null,
): string {
  if (!options) return ''
  if (axes?.length) {
    return axes
      .map((axis) => options[axis.slug])
      .filter(Boolean)
      .join(' / ')
  }
  return Object.values(options).filter(Boolean).join(' / ')
}

export function roundMoney(value: unknown): number {
  const amount = Number(value)
  if (!Number.isFinite(amount)) return 0
  return Math.round(amount * 100) / 100
}

export function productUsesAttributePricing(
  axes: Array<{ pricingMode?: string | null }> | null | undefined,
): boolean {
  return Array.isArray(axes) && axes.some((axis) => axis?.pricingMode === 'attribute')
}

/** Lowest extra on each axis, summed. Listing “from” price for attribute pricing. */
export function attributeFromPriceDelta(
  axes: VariationAttributeConfig[] | null | undefined,
): number {
  if (!productUsesAttributePricing(axes)) return 0
  let total = 0
  for (const axis of axes || []) {
    const values = axis.values || []
    if (!values.length) continue
    let min = Infinity
    for (const value of values) {
      const extra = roundMoney(value.extraPrice)
      if (extra < min) min = extra
    }
    if (Number.isFinite(min)) total += min
  }
  return roundMoney(total)
}

/** Sum of extras for the currently selected value on each axis. */
export function sumAttributeExtras(
  axes: VariationAttributeConfig[] | null | undefined,
  selections: Record<string, string> | null | undefined,
): number {
  return roundMoney(
    attributeSelectionSnapshots(axes, selections).reduce((sum, row) => sum + row.priceDelta, 0),
  )
}

export function attributeSelectionSnapshots(
  axes: VariationAttributeConfig[] | null | undefined,
  selections: Record<string, string> | null | undefined,
): Array<{ fieldId: number; name: string; type: string; value: string; priceDelta: number }> {
  if (!productUsesAttributePricing(axes)) return []
  const rows: Array<{ fieldId: number; name: string; type: string; value: string; priceDelta: number }> = []
  for (const axis of axes || []) {
    const selected = selections?.[axis.slug]
    if (!selected) continue
    const value = (axis.values || []).find((row) => row.name === selected)
    if (!value) continue
    rows.push({
      fieldId: Number(value.id) || 0,
      name: axis.name,
      type: 'text',
      value: value.name,
      priceDelta: roundMoney(value.extraPrice),
    })
  }
  return rows
}

/** Stable cart-line suffix so Alb and Gri Antracit stay separate lines. */
export function variationSelectionsKey(
  selections?: Record<string, string> | null,
): string {
  if (!selections) return ''
  const parts = Object.entries(selections)
    .map(([slug, name]) => [slug.trim(), String(name ?? '').trim()] as const)
    .filter(([slug, name]) => slug && name)
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([slug, name]) => `${encodeURIComponent(slug)}=${encodeURIComponent(name)}`)
  return parts.length ? `var:${parts.join('|')}` : ''
}

export function appendVariationSelectionsKey(
  base: string,
  selections?: Record<string, string> | null,
): string {
  const suffix = variationSelectionsKey(selections)
  const trimmed = (base || '').trim()
  if (trimmed && suffix) return `${trimmed}|${suffix}`
  return suffix || trimmed
}
